import { useState, useEffect } from 'react'
import { Modal } from '../ui/Modal'
import { Avatar } from '../ui/Avatar'
import { usersApi } from '../../api/users'
import { moderationApi } from '../../api/moderation'
import { useUiStore } from '../../stores/uiStore'
import type { User } from '../../types'
import { userName } from '../../lib/displayName'

interface Props {
  user: User
  onClose: () => void
  onCall: (type: 'voice' | 'video') => void
}

export function UserProfileModal({ user, onClose, onCall }: Props) {
  const displayName = userName(user)
  const { addToast } = useUiStore()
  const [isBlocked, setIsBlocked] = useState(false)
  const [blockLoading, setBlockLoading] = useState(false)

  useEffect(() => {
    usersApi.getBlocked()
      .then((r) => {
        const res = r as { blocked?: User[] }
        const blocked = res.blocked ?? []
        setIsBlocked(blocked.some((u) => u.id === user.id))
      })
      .catch(() => {})
  }, [user.id])

  const toggleBlock = async () => {
    setBlockLoading(true)
    try {
      if (isBlocked) {
        await usersApi.unblockUser(user.id)
        setIsBlocked(false)
        addToast('success', `${displayName} разблокирован(а)`)
      } else {
        await usersApi.blockUser(user.id)
        setIsBlocked(true)
        addToast('success', `${displayName} заблокирован(а)`)
      }
    } catch {
      addToast('error', 'Не удалось обновить статус блокировки')
    } finally {
      setBlockLoading(false)
    }
  }

  return (
    <Modal onClose={onClose} title="Профиль" width="max-w-sm">
      <div className="flex flex-col items-center py-6 px-5 gap-4">
        <Avatar name={displayName} src={user.avatar_url} size={88} online={user.online} />

        <div className="text-center">
          <p className="text-txt-primary font-semibold text-lg">{displayName}</p>
          {user.username && (
            <p className="text-txt-meta text-sm mt-0.5">@{user.username}</p>
          )}
          {user.online ? (
            <p className="text-accent text-xs mt-1">в сети</p>
          ) : user.last_seen_at ? (
            <p className="text-txt-meta text-xs mt-1">
              {(() => {
                const d = new Date(user.last_seen_at)
                const now = new Date()
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                const yesterday = new Date(today.getTime() - 86_400_000)
                const t = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
                if (now.getTime() - d.getTime() < 60_000) return 'заходил(а) только что'
                if (d >= today) return `заходил(а) в ${t}`
                if (d >= yesterday) return `заходил(а) вчера в ${t}`
                return `заходил(а) ${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}`
              })()}
            </p>
          ) : null}
        </div>

        {user.bio && (
          <p className="text-txt-secondary text-sm text-center leading-relaxed">{user.bio}</p>
        )}

        {user.phone && (
          <div className="w-full bg-bg-input rounded-xl px-4 py-3">
            <p className="text-txt-meta text-xs mb-0.5">Телефон</p>
            <p className="text-txt-primary text-sm">{user.phone}</p>
          </div>
        )}

        {/* Call buttons */}
        <div className="flex gap-3 w-full">
          <button
            onClick={() => onCall('voice')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl
                       bg-bg-input hover:bg-bg-hover text-txt-primary text-sm font-medium transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.48 2 2 0 0 1 3.6 2.28h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.72 17z"/>
            </svg>
            Звонок
          </button>
          <button
            onClick={() => onCall('video')}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl
                       bg-accent hover:bg-blue-500 text-white text-sm font-medium transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="23 7 16 12 23 17 23 7"/>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
            </svg>
            Видео
          </button>
        </div>

        {/* Block / Unblock */}
        <button
          onClick={toggleBlock}
          disabled={blockLoading}
          className={`w-full py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50
            ${isBlocked
              ? 'bg-bg-input text-txt-secondary hover:bg-bg-hover'
              : 'text-red-400 hover:bg-red-400/10'}`}
        >
          {isBlocked ? 'Разблокировать' : 'Заблокировать'}
        </button>

        {/* Report */}
        <button
          onClick={async () => {
            try {
              await moderationApi.fileReport(user.id, 'User reported', 'abuse')
              addToast('success', 'Жалоба отправлена')
              onClose()
            } catch {
              addToast('error', 'Не удалось отправить жалобу')
            }
          }}
          className="w-full py-2 text-txt-meta text-sm hover:text-txt-secondary transition-colors"
        >
          Пожаловаться
        </button>
      </div>
    </Modal>
  )
}
